import React, { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
} from "@mui/material";
import { useSelector } from "react-redux";
import cameraIcon from "../../Components/Assets/images/camera-switch-front-back.svg";
import { toast } from "react-hot-toast";
import configServ from "../../services/config";
import CameraFrontIcon from "@mui/icons-material/CameraFront";
import CameraRearIcon from "@mui/icons-material/CameraRear";
import { compressImage } from "../../utility/compressImage";

const WebcamDialog = ({ open, onClose, rowData, isChanged }) => {
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cameraOn, setCameraOn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [existingImage, setExistingImage] = useState(null); // 👈 already saved image
  const [facingMode, setFacingMode] = useState("user"); // default front camera
  const toggleCamera = () => {
    setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
  };
  // 🛑 Dialog close hone par reset
  useEffect(() => {
    if (!open) {
      setCapturedImage(null);
      setSelectedFile(null);
      setCameraOn(true);
      setExistingImage(null);
    }
  }, [open]);

  // ✅ Fetch image on open
  useEffect(() => {
    if (open && rowData?.id) {
      fetchImage(rowData.id);
    }
  }, [open, rowData]);

  const fetchImage = async (userId) => {
    try {
      setLoading(true);
      const response = await configServ.getStudentImage({ user_id: userId });

      if (response?.status && response?.data?.image_base64) {
        setExistingImage(response.data.image_base64);
        setCameraOn(false); // start with preview
      } else {
        setExistingImage(null); // no image case
        setCameraOn(true);
      }
    } catch (error) {
      console.error("Image fetch error👉", error);
      setExistingImage(null);
      setCameraOn(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Url = reader.result;
        const compressedImg = await compressImage(base64Url, 0.7);
        console.log(compressedImg);
        setCapturedImage(compressedImg);
        setSelectedFile(compressedImg);
        setCameraOn(false);
        setExistingImage(null); // replace old
      };
      reader.readAsDataURL(file);
    }
  };

  const captureImage = async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    const compressedImg = await compressImage(imageSrc, 0.7);
    // console.log("compressedImg", compressedImg, "imageSrc", imageSrc);
    setCapturedImage(compressedImg);
    setSelectedFile(null);
    setCameraOn(false);
    setExistingImage(null); // replace old
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setSelectedFile(null);
    setCameraOn(true);
    setExistingImage(null); // reset
  };

  const handleSubmit = async () => {
    if (!capturedImage && !selectedFile) {
      toast.error("⚠️ Please capture or upload an image first.");
      return;
    }

    try {
      setLoading(true);

      const dataToSend = {
        user_id: rowData?.id,
        image_url: capturedImage || selectedFile,
      };

      const response = await configServ.storeImage(dataToSend);

      if (response?.status) {
        toast.success("✅ Image saved successfully!");
        setExistingImage(capturedImage || selectedFile); // update local
        isChanged();
      } else {
        toast.error(response?.message || "❌ Failed to save image.");
      }
    } catch (error) {
      console.error("Image Save Error 👉", error);
      toast.error("❌ Something went wrong.");
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {existingImage ? "Update Image" : "Capture / Upload Image"}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%", // parent ke height ke hisaab se
              width: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : existingImage && !capturedImage && !selectedFile ? (
          // Existing image preview
          <img
            src={existingImage}
            alt="Student"
            style={{
              width: "120px", // fixed width
              height: "120px", // fixed height
              borderRadius: "8px",
              objectFit: "cover", // image crop hogi aur box fill karegi
            }}
          />
        ) : cameraOn ? (
          <div>
            <Webcam
              audio={false}
              height={300}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width="100%"
              style={{ borderRadius: "8px" }}
              videoConstraints={{
                facingMode: facingMode,
              }}
            />

            <Button
              // variant="contained"
              onClick={toggleCamera}
              startIcon={
                <img
                  src={facingMode === "user" ? cameraIcon : cameraIcon}
                  alt="camera icon"
                  style={{ width: 30, height: 30 }}
                />
              }
              style={{ marginTop: 10 }}
            ></Button>
          </div>
        ) : capturedImage ? (
          <img
            src={capturedImage}
            alt="Preview"
            style={{ width: "100%", borderRadius: "8px" }}
          />
        ) : null}

        <Grid
          container
          alignItems="center"
          justifyContent="center"
          spacing={2}
          style={{ marginTop: "8px" }}
        >
          {existingImage && !capturedImage && !selectedFile ? (
            <Grid item xs={12}>
              <Button
                onClick={handleRetake}
                style={{ width: "100%" }}
                variant="outlined"
                color="secondary"
              >
                Change / Re-upload
              </Button>
            </Grid>
          ) : cameraOn ? (
            <>
              <Grid item xs={6}>
                <Button
                  onClick={captureImage}
                  style={{ width: "100%" }}
                  variant="contained"
                  color="primary"
                >
                  Capture Image
                </Button>
              </Grid>
              <Grid item xs={6}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                  id="fileInput"
                />
                <label htmlFor="fileInput" style={{ width: "100%" }}>
                  <Button
                    component="span"
                    style={{ width: "100%" }}
                    variant="contained"
                    color="primary"
                  >
                    Choose File
                  </Button>
                </label>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Button
                onClick={handleRetake}
                style={{ width: "100%" }}
                variant="outlined"
                color="secondary"
              >
                Retake / Re-upload
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {(!existingImage || capturedImage || selectedFile) && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            disabled={(!capturedImage && !selectedFile) || loading}
          >
            {loading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : existingImage ? (
              "Update"
            ) : (
              "Submit"
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WebcamDialog;
